import React from "react";
import { formatDateWithTime } from "../../../utils/dateFormat";
import { fieldLabel, tableLabel } from "./constants";

/*
 * Presentational only — it renders the normalized groups from buildComparisonGroups and
 * knows nothing about payload_version. Shared by v0 and v1.
 *
 * Responsive: a real table on >=md, stacked cards on mobile (a 3-column table forces
 * horizontal scrolling on a phone, which makes an approve/reject decision harder to read).
 * Meaning is never carried by colour alone — each side is also labelled and iconed, so it
 * stays readable for colourblind users.
 */

const renderValue = (key = "", val) => {
  if (val === null || val === undefined || val === "") return <span className="text-gray-400">—</span>;
  if (val === true) return "true";
  if (val === false) return "false";
  if (key?.toLowerCase?.().includes("date") && (typeof val === "number" || !Number.isNaN(Number(val)))) {
    const ts = typeof val === "number" ? val : Math.floor(new Date(val).getTime() / 1000);
    if (ts) return formatDateWithTime(ts);
  }
  if (typeof val === "object") {
    return (
      <pre className="whitespace-pre-wrap break-all bg-gray-50 p-2 rounded text-xs max-h-40 overflow-y-auto">
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  }
  return String(val);
};

const ComparisonTable = ({
  groups,
  emptyLabel = "No changes to show.",
  /*
   * Optional per-screen presentation hooks, so domain-specific labelling lives with the
   * screen that needs it instead of forking this component.
   *   formatValue(field, value) -> node, or undefined to fall back to the default renderer
   *   formatField(field)        -> string, or undefined to fall back to fieldLabel()
   * Brands use these to show `Sponsored` / `Active` instead of raw ids.
   */
  formatValue,
  formatField,
}) => {
  const display = (field, value) => {
    if (formatValue) {
      const formatted = formatValue(field, value);
      if (formatted !== undefined) return formatted;
    }
    return renderValue(field, value);
  };
  const label = (field) => formatField?.(field) ?? fieldLabel(field);

  const BeforeValue = ({ field, value }) => (
    <span className="text-gray-500 line-through decoration-gray-300 break-words">{display(field, value)}</span>
  );

  const AfterValue = ({ field, value }) => (
    <span className="text-emerald-700 font-medium break-words">{display(field, value)}</span>
  );

  if (!groups?.length) {
    return (
      <div className="text-center py-8 px-4 border border-dashed border-gray-200 rounded-lg">
        <p className="text-sm text-gray-500">{emptyLabel}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.table} aria-label={tableLabel(group.table)}>
          {/* Group heading is only meaningful when an action spans multiple tables,
              but it is always rendered so the layout stays consistent. */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-sm font-semibold text-gray-700">{tableLabel(group.table)}</h3>
            <span className="text-xs text-gray-400">
              {group.rows.length} {group.rows.length === 1 ? "change" : "changes"}
            </span>
          </div>

          {/* ---- mobile: stacked cards ---- */}
          <ul className="md:hidden space-y-3">
            {group.rows.map((row) => (
              <li key={row.field} className="border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2 break-words">{label(row.field)}</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex gap-2">
                    <span aria-hidden="true" className="text-gray-400 shrink-0">−</span>
                    <span className="text-xs uppercase tracking-wide text-gray-400 shrink-0 w-16">Before</span>
                    <BeforeValue field={row.field} value={row.before} />
                  </div>
                  <div className="flex gap-2">
                    <span aria-hidden="true" className="text-emerald-600 shrink-0">+</span>
                    <span className="text-xs uppercase tracking-wide text-emerald-600 shrink-0 w-16">After</span>
                    <AfterValue field={row.field} value={row.after} />
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* ---- md+: table ----
              NOTE: deliberately `max-md:hidden` and NOT `hidden md:block`. Something in the
              global CSS defines `.hidden{display:none!important}` (on top of Tailwind's own
              `.hidden{display:none}`), and `.md\:block` carries no !important, so it can
              never win -- `hidden md:block` stays invisible at EVERY width. `max-md:hidden`
              emits a different class (`.max-md\:hidden`), so there is no collision. */}
          <div className="max-md:hidden overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th scope="col" className="text-left px-3 py-2 font-semibold text-gray-600 w-1/4">Field</th>
                  <th scope="col" className="text-left px-3 py-2 font-semibold text-gray-500">Before</th>
                  <th scope="col" className="text-left px-3 py-2 font-semibold text-emerald-700">After</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <tr key={row.field} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 align-top">
                    <th scope="row" className="text-left px-3 py-2 font-medium text-gray-700 break-words">
                      {label(row.field)}
                    </th>
                    <td className="px-3 py-2 break-words"><BeforeValue field={row.field} value={row.before} /></td>
                    <td className="px-3 py-2 break-words"><AfterValue field={row.field} value={row.after} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
};

export default ComparisonTable;
